import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

export default function AuthModal({ onClose, defaultTab = 'login' }) {
  const { login, register, googleVerify } = useAuth();
  const [tab, setTab] = useState(defaultTab); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      if (tab === 'forgot') {
        await apiFetch('/api/auth/forgot-password', { method: 'POST', body: { email: email.trim().toLowerCase() } });
        setSent(true);
      } else if (tab === 'register') {
        await register(email.trim().toLowerCase(), password, name.trim() || email.split('@')[0]);
        onClose?.();
      } else {
        await login(email.trim().toLowerCase(), password);
        onClose?.();
      }
    } catch (ex) {
      setErr(ex.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (credentialResponse) => {
    setErr('');
    setBusy(true);
    try {
      await googleVerify(credentialResponse.credential);
      onClose?.();
    } catch (ex) {
      setErr(ex.message || 'Google sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{tab === 'register' ? 'Create your account' : tab === 'forgot' ? 'Reset your password' : 'Welcome back'}</h3>
            <p className="text-sm text-gray-500 mt-1">{tab === 'forgot' ? "We'll email you a secure reset link." : 'Save trips, favorite restaurants, unlock Premium.'}</p>
          </div>
          <button data-testid="close-auth-modal" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" aria-label="Close">×</button>
        </div>

        {tab === 'forgot' ? (
          sent ? (
            <div data-testid="forgot-sent-msg" className="text-center py-4">
              <div className="text-4xl mb-3">📧</div>
              <p className="text-gray-800 font-semibold mb-1">Check your inbox</p>
              <p className="text-sm text-gray-500 mb-5">If an account exists for <strong>{email}</strong>, a password reset link is on its way. It expires in 60 minutes.</p>
              <button data-testid="forgot-back-btn" onClick={() => { setTab('login'); setSent(false); setErr(''); }} className="text-teal-600 font-semibold hover:underline">← Back to sign in</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="forgot-email" type="email" required placeholder="Your account email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
              {err && <div data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
              <button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full bg-teal-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors">{busy ? 'Sending…' : 'Send reset link'}</button>
              <button type="button" data-testid="forgot-back-btn" onClick={() => { setTab('login'); setErr(''); }} className="w-full text-sm text-gray-500 hover:text-teal-600">← Back to sign in</button>
            </form>
          )
        ) : (
        <>
        <div data-testid="google-signin-btn" className="w-full flex justify-center mb-4">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setErr('Google sign-in failed. Please try again.')}
            theme="outline"
            size="large"
            width="360"
            text={tab === 'register' ? 'signup_with' : 'signin_with'}
            shape="pill"
          />
        </div>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400">or with email</span></div>
        </div>

        <div className="flex gap-2 mb-4 text-sm">
          <button
            data-testid="tab-login"
            onClick={() => setTab('login')}
            className={`flex-1 py-2 rounded-lg font-semibold ${tab === 'login' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >Sign in</button>
          <button
            data-testid="tab-register"
            onClick={() => setTab('register')}
            className={`flex-1 py-2 rounded-lg font-semibold ${tab === 'register' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >Create account</button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {tab === 'register' && (
            <input data-testid="auth-name" type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          )}
          <input data-testid="auth-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          <input data-testid="auth-password" type="password" required minLength={8} placeholder="Password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          {err && <div data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
          <button data-testid="auth-submit" type="submit" disabled={busy} className="w-full bg-teal-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors">
            {busy ? 'Please wait…' : tab === 'register' ? 'Create account' : 'Sign in'}
          </button>
          {tab === 'login' && (
            <button type="button" data-testid="forgot-password-link" onClick={() => { setTab('forgot'); setErr(''); }} className="w-full text-sm text-teal-600 hover:underline pt-1">Forgot your password?</button>
          )}
        </form>
        </>
        )}
      </div>
    </div>
  );
}
